import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, X } from 'lucide-react'
import { useUpgradePlanEligibility } from '../../hooks/credits/useUpgradePlanEligibility'
import { UpgradePlanCompareModal } from './UpgradePlanCompareModal'

const TOPBAR_DISMISS_KEY = 'free_upgrade_topbar_dismissed'

interface Props {
  variant: 'topbar' | 'inline'
}

export function UpgradePlanCompactCta({ variant }: Props) {
  const { t } = useTranslation('credits')
  const [modalOpen, setModalOpen] = useState(false)
  const [dismissed, setDismissed] = useState(
    () => variant === 'topbar' && sessionStorage.getItem(TOPBAR_DISMISS_KEY) === '1',
  )

  const { show, tierCode, upgradePath, currentCompareTier } = useUpgradePlanEligibility({
    dismissed: variant === 'topbar' ? dismissed : false,
  })

  if (!show) return null

  const dismiss = () => {
    sessionStorage.setItem(TOPBAR_DISMISS_KEY, '1')
    setDismissed(true)
  }

  const ctaLabel =
    tierCode === 'STANDARD' ? t('upgradeTopBar.ctaStandard') : t('upgradeTopBar.cta')
  const ctaShort =
    tierCode === 'STANDARD' ? t('upgradeTopBar.ctaStandardShort') : t('upgradeTopBar.ctaShort')

  if (variant === 'inline') {
    return (
      <>
        <div className="flex flex-col gap-3 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 via-amber-50/80 to-orange-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-lg bg-white/80 p-2 shrink-0 border border-orange-100">
              <Sparkles className="h-4 w-4 text-[#FF5A00]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">{ctaLabel}</p>
              <p className="text-xs text-gray-600 mt-0.5">{t('upgradeTopBar.inlineHint')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#212121] px-4 py-2 text-sm font-semibold text-white hover:bg-[#333333] transition-colors shrink-0"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#FF5A00]" />
            {t('upgradeTopBar.inlineButton')}
          </button>
        </div>

        <UpgradePlanCompareModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          currentTier={currentCompareTier}
          upgradePath={upgradePath}
        />
      </>
    )
  }

  return (
    <>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#FF5A00]/45 bg-[#FF5A00]/10 px-2 py-1 text-xs font-semibold text-orange-200 hover:bg-[#FF5A00]/20 hover:text-white transition-colors sm:px-2.5 sm:py-1.5 sm:text-sm max-w-[11rem] sm:max-w-none"
          title={ctaLabel}
        >
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#FF5A00]" />
          <span className="hidden md:inline truncate">{ctaLabel}</span>
          <span className="md:hidden truncate">{ctaShort}</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
          aria-label={t('upgradeTopBar.dismiss')}
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      <UpgradePlanCompareModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        currentTier={currentCompareTier}
        upgradePath={upgradePath}
      />
    </>
  )
}
